/**
 * DebugUIManager - Manages the debug heads-up display (HUD)
 * Shows tile indices overlay and game state information in the sidebar
 */

import Phaser from "phaser";
import { TileIndex } from "../types";

/**
 * Debug UI state
 */
export type DebugUIManagerState = {
  elements: Phaser.GameObjects.GameObject[];
  tileIndexTexts: Phaser.GameObjects.Text[];
  tileIndexBackgrounds: Phaser.GameObjects.Rectangle[];
  debugInfoTexts: Phaser.GameObjects.Text[];
  scrollContainer: Phaser.GameObjects.Container | null;
  scrollMask: Phaser.GameObjects.Graphics | null;
  scrollY: number;
  maxScrollY: number;
  panelX: number;
  panelWidth: number;
  panelHeight: number;
};

/**
 * Configuration for creating debug UI
 */
export type DebugUIConfig = {
  viewportWidth: number;
  viewportHeight: number;
  panelWidth: number;
  panelX: number;
  levelData: TileIndex[][];
  entityMap: TileIndex[][];
  tileSize: number;
  debugInfo: DebugInfo;
};

/**
 * Debug information to display
 */
export type DebugInfo = {
  hasWon: boolean;
  isWaitingToStart: boolean;
  isShowingHint: boolean;
  isCustomLevel: boolean;
};

/**
 * Create debug UI with tile overlays and info panel
 */
export function createDebugUI(
  scene: Phaser.Scene,
  config: DebugUIConfig
): DebugUIManagerState {
  const {
    viewportWidth,
    viewportHeight,
    panelWidth,
    panelX,
    levelData,
    entityMap,
    tileSize,
    debugInfo,
  } = config;

  const elements: Phaser.GameObjects.GameObject[] = [];

  // Create tile index overlays
  const { texts, backgrounds } = createTileIndexOverlays(
    scene,
    levelData,
    entityMap,
    tileSize
  );
  elements.push(...texts, ...backgrounds);

  // Create debug info panel with scrolling
  const { texts: infoTexts, container, mask } = createDebugInfoPanel(
    scene,
    panelX,
    panelWidth,
    viewportHeight,
    debugInfo
  );

  if (container) elements.push(container);
  if (mask) elements.push(mask);

  // Calculate max scroll based on content height
  const contentHeight = infoTexts.length * 14; // 14px line height
  const visibleHeight = viewportHeight - 140; // Leave room for buttons
  const maxScrollY = Math.max(0, contentHeight - visibleHeight);

  const state: DebugUIManagerState = {
    elements,
    tileIndexTexts: texts,
    tileIndexBackgrounds: backgrounds,
    debugInfoTexts: infoTexts,
    scrollContainer: container,
    scrollMask: mask,
    scrollY: 0,
    maxScrollY,
    panelX,
    panelWidth,
    panelHeight: viewportHeight,
  };

  // Set up mouse wheel scrolling
  setupScrolling(scene, state);

  return state;
}

/**
 * Destroy all debug UI elements
 */
export function destroyDebugUI(state: DebugUIManagerState): void {
  // Remove scroll event listener
  state.elements.forEach((element) => element.destroy());
  state.tileIndexTexts.forEach((text) => text.destroy());
  state.tileIndexBackgrounds.forEach((bg) => bg.destroy());
  state.debugInfoTexts.forEach((text) => text.destroy());
  if (state.scrollContainer) state.scrollContainer.destroy();
  if (state.scrollMask) state.scrollMask.destroy();
}

/**
 * Update debug info display in real-time
 */
export function updateDebugInfo(
  state: DebugUIManagerState,
  debugInfo: DebugInfo
): void {
  const lines = formatDebugInfo(debugInfo);

  // Update existing text objects
  lines.forEach((line, index) => {
    if (state.debugInfoTexts[index]) {
      state.debugInfoTexts[index].setText(line);
    }
  });
}

/**
 * Create tile index overlays for all tiles (shows base tile / entity tile)
 */
function createTileIndexOverlays(
  scene: Phaser.Scene,
  levelData: TileIndex[][],
  entityMap: TileIndex[][],
  tileSize: number
): {
  texts: Phaser.GameObjects.Text[];
  backgrounds: Phaser.GameObjects.Rectangle[];
} {
  const texts: Phaser.GameObjects.Text[] = [];
  const backgrounds: Phaser.GameObjects.Rectangle[] = [];

  for (let row = 0; row < levelData.length; row++) {
    for (let col = 0; col < levelData[row].length; col++) {
      const baseTile = levelData[row][col];
      const entityTile = entityMap[row][col];

      // Calculate tile center position
      const centerX = col * tileSize + tileSize / 2;
      const centerY = row * tileSize + tileSize / 2;

      // Position text at top-left corner
      const textX = centerX - 18;
      const textY = centerY - 18;

      // Display "base/entity" format
      const displayText = entityTile === 0 ? String(baseTile) : `${baseTile}/${entityTile}`;

      // Create dark background (wider to accommodate entity values)
      const bgWidth = entityTile === 0 ? 20 : 32;
      const bg = scene.add.rectangle(
        textX + (bgWidth / 2), // Center background
        textY + 6,
        bgWidth,
        12,
        0x000000,
        0.7
      );
      bg.setScrollFactor(1); // Scroll with world
      backgrounds.push(bg);

      // Create text
      const text = scene.add.text(textX, textY, displayText, {
        fontSize: "10px",
        color: "#ffffff",
        fontFamily: "Arial",
        fontStyle: "bold",
      });
      text.setScrollFactor(1); // Scroll with world
      texts.push(text);
    }
  }

  return { texts, backgrounds };
}

/**
 * Create debug info panel with scrollable content
 */
function createDebugInfoPanel(
  scene: Phaser.Scene,
  panelX: number,
  panelWidth: number,
  panelHeight: number,
  debugInfo: DebugInfo
): {
  texts: Phaser.GameObjects.Text[];
  container: Phaser.GameObjects.Container | null;
  mask: Phaser.GameObjects.Graphics | null;
} {
  const texts: Phaser.GameObjects.Text[] = [];
  const lines = formatDebugInfo(debugInfo);

  // Create container for scrollable content
  const container = scene.add.container(panelX, 20);
  container.setScrollFactor(0);

  // Create text objects for each line
  const lineHeight = 14;
  lines.forEach((line, index) => {
    const text = scene.add.text(10, index * lineHeight, line, {
      fontSize: "11px",
      color: "#ffff00",
      fontFamily: "Arial",
      fontStyle: "normal",
      stroke: "#000000",
      strokeThickness: 1,
    });
    texts.push(text);
    container.add(text);
  });

  // Create mask to clip content (leave 140px for buttons at bottom)
  const mask = scene.make.graphics({});
  mask.fillStyle(0xffffff);
  mask.fillRect(panelX, 20, panelWidth, panelHeight - 140);
  container.setMask(new Phaser.Display.Masks.GeometryMask(scene, mask));

  return { texts, container, mask };
}

/**
 * Set up mouse wheel scrolling for debug panel
 */
function setupScrolling(
  scene: Phaser.Scene,
  state: DebugUIManagerState
): void {
  scene.input.on(
    "wheel",
    (
      pointer: Phaser.Input.Pointer,
      gameObjects: Phaser.GameObjects.GameObject[],
      deltaX: number,
      deltaY: number,
      deltaZ: number
    ) => {
      // Check if pointer is over the panel
      if (
        pointer.x >= state.panelX &&
        pointer.x <= state.panelX + state.panelWidth
      ) {
        state.scrollY = Phaser.Math.Clamp(
          state.scrollY + deltaY * 0.5,
          0,
          state.maxScrollY
        );
        if (state.scrollContainer) {
          state.scrollContainer.setY(20 - state.scrollY);
        }
      }
    }
  );
}

/**
 * Format debug info into display lines
 */
function formatDebugInfo(debugInfo: DebugInfo): string[] {
  const lines: string[] = [];

  lines.push("DEBUG INFO");
  lines.push("──────────");
  lines.push("");

  // Boolean states
  lines.push(`hasWon: ${debugInfo.hasWon}`);
  lines.push(`isWaitingToStart: ${debugInfo.isWaitingToStart}`);
  lines.push(`isShowingHint: ${debugInfo.isShowingHint}`);
  lines.push("");

  lines.push(`isCustomLevel: ${debugInfo.isCustomLevel}`);
  lines.push("");

  return lines;
}

